import { useNavigate } from "react-router-dom";  
import React, { useState } from "react";  
import { createUser, verifyOtp, loginUser as apiLoginUser } from '../../api/user';  
import { Alert, Button, Card, Form, Input, notification, Typography, Modal, Space, message } from "antd";  
import { UserAddOutlined, CheckCircleOutlined } from "@ant-design/icons";  
import { saveState } from '../../stores/store';  
import { fetchAndSetCompetition } from '../../helpers/fetchAndSetCompetition';  
import { fetchAndSetSettings } from '../../helpers/fetchAndSetSettings';  
import { useSettings } from '../../contexts/settingsContext';  
import dayjs from 'dayjs';  
  
const { Text, Title } = Typography;  
  
const Register = () => {  
    const navigate = useNavigate();  
    const [api, contextHolder] = notification.useNotification();  
    const [form] = Form.useForm();  
    const [errorMessage, setErrorMessage] = useState("");  
    const [isModalVisible, setIsModalVisible] = useState(false);  
    const [pendingUser, setPendingUser] = useState(null);  
    const [otp, setOtp] = useState('');  
    const [verifying, setVerifying] = useState(false);  
    const [verifyError, setVerifyError] = useState(null);  
    const [submitting, setSubmitting] = useState(false);  
  
    const { dispatch, state: settings } = useSettings();  
  
    // Removed province-related states and fetch logic.  
  
    const formData = {  
        first_name: "",  
        last_name: "",  
        email: "",  
        cell_number: "",  
        work_number: "",  
        password: "",  
        confirm_password: ""  
    };  
  
    const registerUser = async (values) => {  
        const { first_name, last_name, email, cell_number, work_number, password, confirm_password } = values;  
        if (password !== confirm_password) {  
            setErrorMessage("Passwords do not match.");  
            return;  
        }  
        // No activeProvince required now.  
        const newUser = { first_name, last_name, email, cell_number, work_number, password, role: "guardian", applicationCompleted: true };  
        try {  
            setSubmitting(true);  
            const response = await createUser(newUser);  
            setPendingUser({ ...newUser, userId: response.user._id });  
  
            // Directly log the user in after registration  
            const data = await apiLoginUser(email, password);  
            saveState(data);  
            await fetchAndSetCompetition(dispatch);  
            const selectedCompetition = JSON.parse(localStorage.getItem('selectedCompetition'));  
            if (selectedCompetition) {  
                await fetchAndSetSettings(dispatch, selectedCompetition._id);  
            }  
  
            navigate("/contestants", { replace: true });  
            setSubmitting(false);  
        } catch (err) {  
            let description = "An error occurred while creating the user.";  
            if (err.message) {  
                if (  
                    err.message.includes("Email already in use") ||  
                    err.message.includes("Email already in use and not verified")  
                ) {  
                    description = err.message.replace("Error creating user: ", '');  
                }  
            }  
            setErrorMessage(description);  
            api.error({  
                message: "Registration Error",  
                description,  
                placement: "topRight"  
            });  
            setSubmitting(false);  
        }  
    };  
  
    const handleOtpChange = (text) => {  
        setOtp(text);  
    };  
    const sharedProps = { handleOtpChange };  
  
    const handleModalOk = async () => {  
        try {  
            setVerifying(true);  
            setVerifyError(null);  
            const { email, password } = pendingUser;  
            const otpResponse = await verifyOtp(email, otp);  
            if (otpResponse.message === 'Email verified successfully') {  
                const data = await apiLoginUser(email, password);  
                saveState(data);  
                await fetchAndSetCompetition(dispatch);  
                const selectedCompetition = JSON.parse(localStorage.getItem('selectedCompetition'));  
                if (selectedCompetition) {  
                    await fetchAndSetSettings(dispatch, selectedCompetition._id);  
                }  
                setIsModalVisible(false);  
                navigate("/contestants", { replace: true });  
            } else {  
                setVerifyError('Invalid OTP. Please try again.');  
            }  
            setVerifying(false);  
        } catch (error) {  
            console.error('Error during OTP verification:', error);  
            message.error('The OTP you entered is invalid.');  
            setVerifyError('The OTP you entered is invalid.');  
            setVerifying(false);  
        }  
    };  
  
    return (  
        <div className="frontend-form">  
            {contextHolder}  
            <div className='frontend-form-graphic' style={{ backgroundImage: 'url("/images/SACOPAPortalBackground.svg")' }}></div>  
            <Card className="frontend-form-container">  
                <Title level={3}>Register as Guardian / Coach</Title>  
                <Text type="secondary">Please fill out the form below to sign up as a coach or a guardian. You will be able to add your contestants and groups once you have logged in.</Text>  
                <div className="steps-content">  
                    <Form layout='vertical' form={form} onFinish={registerUser}>  
                        <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>  
                            <Input placeholder="First Name" />  
                        </Form.Item>  
                        <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>  
                            <Input placeholder="Last Name" />  
                        </Form.Item>  
                        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>  
                            <Input placeholder="Email" />  
                        </Form.Item>  
                        <Form.Item name="cell_number" label="Cell Number" rules={[{ required: true }]}>  
                            <Input placeholder="Cell Number" />  
                        </Form.Item>  
                        <Form.Item name="work_number" label="Work Number">  
                            <Input placeholder="Work Number" />  
                        </Form.Item>  
                        <Form.Item name="password" label="Password" rules={[{ required: true }]}>  
                            <Input.Password placeholder="Password" />  
                        </Form.Item>  
                        <Form.Item  
                            name="confirm_password"  
                            label="Confirm Password"  
                            rules={[  
                                { required: true, message: 'Please confirm your password!' },  
                                ({ getFieldValue }) => ({  
                                    validator(_, value) {  
                                        if (!value || getFieldValue('password') === value) {  
                                            return Promise.resolve();  
                                        }  
                                        return Promise.reject(new Error('The two passwords do not match!'));  
                                    },  
                                }),  
                            ]}  
                        >  
                            <Input.Password placeholder="Confirm Password" autoComplete="off" />  
                        </Form.Item>  
                        {errorMessage && <Form.Item><Alert type="error" message={errorMessage} /></Form.Item>}  
                        <Form.Item>  
                            <Button type="primary" htmlType="submit" icon={<UserAddOutlined />} loading={submitting}>Sign Up</Button>  
                        </Form.Item>  
                    </Form>  
                </div>  
            </Card>  
  
            {/* Email Verification Modal */}  
            <Modal  
                title="Email Verification"  
                open={isModalVisible}  
                onOk={handleModalOk}  
                onCancel={() => {  
                    setIsModalVisible(false);  
                    setOtp('');  
                    setVerifyError(null);  
                }}  
                okText="Verify and Proceed"  
                confirmLoading={verifying}  
                okButtonProps={{  
                    icon: <CheckCircleOutlined />,  
                    disabled: otp.length !== 4,  
                }}  
            >  
                <Space direction='vertical' size="middle" style={{ width: '100%' }}>  
                    <Text>Please enter the OTP that was sent to your email to verify your account.</Text>  
                    <Input.OTP  
                        placeholder="Enter OTP"  
                        value={otp}  
                        size='large'  
                        onChange={handleOtpChange}  
                        length={4}  
                        {...sharedProps}  
                        style={{ width: '100%', letterSpacing: '0.5em' }}  
                    />  
                    {verifyError && <Alert showIcon type='error' message={verifyError} />}  
                </Space>  
            </Modal>  
        </div>  
    );  
};  
  
export default Register;  